import { Button, Flex, GetProps, Tree, TreeDataNode, Typography } from 'antd';
import { FileOutlined, FolderOutlined, PlusOutlined, PlusSquareOutlined } from '@ant-design/icons';
import React, { useEffect, useState } from 'react';
import {
  TQuestionBankCategory,
  TQuestionBankCategorySubTree,
  TQuestionBankCategoryTree,
} from '@/types/QuestionBank';
import { useRequest } from '@@/plugin-request/request';
import { createQuestionBankCategory, getQuestionBankCategoryTree } from '@/services/QuestionBank';
import cx from 'classnames';
import styles from './index.less';
import { createName } from '@/utils';
import { isEmpty } from 'lodash';

const QuestionBankCategory = ({
  onCreate,
  onSelect: propsOnSelect,
  refresh,
  setRefresh,
}: {
  onCreate?: (created: TQuestionBankCategory) => void;
  onSelect?: (selected?: TQuestionBankCategory) => void;
  refresh: number;
  setRefresh: (refresh: number) => void;
}) => {
  type DirectoryTreeProps = GetProps<typeof Tree.DirectoryTree>;
  const [questionBankCategoryTree, setQuestionBankCategoryTree] =
    useState<TQuestionBankCategoryTree>();
  const questionBankCategoryMap = new Map<string, TQuestionBankCategory>();
  const names: string[] = [];
  const [selectedKey, setSelectedKey] = useState<React.Key>();
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const empty = isEmpty(questionBankCategoryTree?.questionBankCategories);

  const { run: getQuestionBankCategoryTreeRun } = useRequest(
    () => {
      return getQuestionBankCategoryTree();
    },
    {
      manual: true,
      onSuccess: (data) => {
        setQuestionBankCategoryTree(data);
      },
    },
  );

  const buildTree = (item: TQuestionBankCategorySubTree, level: number): TreeDataNode => {
    if (!questionBankCategoryMap.has(item.id!)) {
      questionBankCategoryMap.set(item.id!, item);
    }
    names.push(item.name);

    return {
      title:
        level === 3 ? (
          item.name
        ) : (
          <Flex gap={'middle'} justify={'space-between'} align={'center'}>
            <Typography.Text
              ellipsis={{ tooltip: item.name }}
              className={cx('flex-1', 'w-[0px]', 'text-[#595959]')}
            >
              {item.name}
            </Typography.Text>
            <PlusOutlined
              className={cx('text-[#1890FF]', styles.AddBtn)}
              onClick={(event) => {
                event.stopPropagation();
                createQuestionBankCategoryRun(createName(names, '未命名分类'), 1, item.id);
              }}
            />
          </Flex>
        ),
      key: item.id!,
      icon:
        level == 3 ? (
          <FileOutlined className={'text-[#595959]'} />
        ) : (
          <FolderOutlined className={'text-[#595959]'} />
        ),
      children: item.children
        ? item.children.map((child) => buildTree(child, level + 1))
        : undefined,
    };
  };

  useEffect(() => {
    getQuestionBankCategoryTreeRun();
  }, [refresh]);

  const treeData = questionBankCategoryTree?.questionBankCategories?.map((item) =>
    buildTree(item, 1),
  );

  const onSelect: DirectoryTreeProps['onSelect'] = (keys, info) => {
    if (keys[0]) {
      setSelectedKey(keys[0]);
      propsOnSelect?.(questionBankCategoryMap.get(keys[0].toString()));
    }
  };

  const { run: createQuestionBankCategoryRun } = useRequest(
    (name: string, index: number, pid?: string) => {
      return createQuestionBankCategory({ name, pid, index });
    },
    {
      manual: true,
      onSuccess: (data) => {
        propsOnSelect?.(data);
        onCreate?.(data);
        setSelectedKey(data.id);
        setRefresh(refresh + 1);
        if (data.pid) {
          setExpandedKeys([data.pid, ...expandedKeys]);
        }
      },
    },
  );

  return (
    <Flex gap="middle" vertical>
      <Flex
        justify="space-between"
        align={'center'}
        className={cx('mt-[16px]', 'ml-[24px]', 'mr-[24px]')}
      >
        <p className={cx('flex-1', 'text-base', 'font-medium')}>题库分类</p>
        <Button
          type="link"
          icon={<PlusSquareOutlined />}
          className={cx('text-sm', 'font-normal', 'text-[#1890FF]')}
          onClick={() => {
            createQuestionBankCategoryRun(createName(names, '未命名分类'), 1);
          }}
        >
          新建一级分类
        </Button>
      </Flex>
      {empty ? (
        <div className={cx('text-center', 'mt-[32px]')}>
          <img
            src="https://hwobs.fanruan.com/ui/question.png"
            className={cx('w-[160px]', 'h-[160px]')}
            alt="question"
          />
          <p className={cx('text-[#C0C0C0]', 'text-[14px]', 'font-normal')}>暂无分类</p>
          <p className={cx('text-[#C0C0C0]', 'text-[14px]', 'font-normal')}>
            请点击上方按钮新建分类
          </p>
        </div>
      ) : (
        <Flex justify="left" className={styles.TreeWrapper}>
          <Tree
            selectedKeys={[selectedKey || '']}
            expandedKeys={expandedKeys}
            onExpand={(keys) => {
              setExpandedKeys(keys);
            }}
            showIcon
            defaultExpandAll
            onSelect={onSelect}
            treeData={treeData}
          />
        </Flex>
      )}
    </Flex>
  );
};

export default QuestionBankCategory;
